import IndexStore from 'src/stores/index';
import type {TextQuestion} from 'src/types/survey';
import type {DynamicLabels} from 'src/action-creators/dynamic-labels';

import * as React from 'react';
import _get from 'lodash/get';

import {values} from 'src/utils/object';

import {
  WritebackContext,
  UPDATE_QUESTION,
  UPDATE_QUESTION_TYPE,
  RECEIVE_QUESTION,
} from 'src/components/writeback/writeback-wrapper';
import {
  MultipleChoice,
  RatingScale,
  FreeResponse,
  CalendarDate,
} from 'src/components/workflow/event/content/module/editors/writeback-modules';
import DraftInput from 'src/components/lib/draft-input';
import SurveyModuleOptions from 'src/components/workflow/event/content/survey-module-options';
import Loading from 'src/components/lib/loading/loading';

import WarningIcon from 'src/images/invalid-number.svg';

import moduleCss from './module.css';


type Props = {
  fluxStore: IndexStore;
  error?: Record<any, any>;
  moduleData: TextQuestion;
  conditionalAlerts?: boolean;
  dynamicLabels: DynamicLabels;
};

const questionComponents = {
  text_survey_question: FreeResponse,
  nps_survey_question: RatingScale,
  rating_scale_survey_question: RatingScale,
  multiple_choice_survey_question: MultipleChoice,
  calendar_date_survey_question: CalendarDate,
} as const;

const FieldWritebackModule = ({
  fluxStore,
  error,
  moduleData,
  conditionalAlerts,
  dynamicLabels,
}: Props):
  | React.ReactElement<React.ComponentProps<'div'>>
  | React.ReactNode => {
  const {state, dispatch} = React.useContext(WritebackContext);

  React.useEffect(() => {
    dispatch({
      type: RECEIVE_QUESTION,
      payload: moduleData,
    });
  }, []);

  const currentQuestionType = state.question?.selectedType;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'QuestionType' can't be used to index type 'CurrentQuestion'.
  const currentQuestion = state.question?.[currentQuestionType];

  if (!currentQuestion || !state.attribute_meta_data) {
    return <Loading />;
  }

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'QuestionType' can't be used to index type '{ readonly text_survey_question: ({ conditionalAlerts, }: { conditionalAlerts?: boolean | undefined; }) => ReactElement<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, string | JSXElementConstructor<...>>; readonly nps_survey_question: ({ conditionalAlerts, error, }: { ...; }) => ReactElement<...>...'.
  const QuestionComponent = questionComponents[currentQuestionType];

  const errorStrings = error?.question?.errors ?? [];

  return (
    <div className={moduleCss.generalModule_content}>
      <label
        className={moduleCss.generalModule_questionLabel}
        data-qa-id="module-question-input"
      >
        <div className={moduleCss.labelWithInlineSelector}>Question Text</div>
        {/* @ts-expect-error - TS2786 - 'DraftInput' cannot be used as a JSX component. */}
        <DraftInput
          className={moduleCss.generalModule_input}
          value={currentQuestion.question}
          hasError={error?.question?.errors?.length}
          placeholder="What could we do to make your job better?"
          dynamicLabels={dynamicLabels}
          // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
          onChange={(value) => {
            dispatch({
              type: UPDATE_QUESTION,
              payload: {
                questionType: currentQuestionType,
                // @ts-expect-error - TS2322 - Type '{ question: any; }' is not assignable to type 'Question'.
                attrs: {
                  question: value,
                },
              },
            });
          }}
          showAddButton
          multiEntityEnabled
        />
      </label>
      {errorStrings.length > 0 && (
        <div className={moduleCss.errors}>
          {/* @ts-expect-error - TS7006 - Parameter 'error' implicitly has an 'any' type. */}
          {errorStrings.map((error) => (
            <div className={moduleCss.error}>{error}</div>
          ))}
        </div>
      )}
      <SurveyModuleOptions
        // @ts-expect-error - TS2322 - Type '{ error: Record<any, any> | undefined; questionId: string; fluxStore: IndexStore; }' is not assignable to type 'IntrinsicAttributes & { fluxStore: IndexStore; }'.
        error={error}
        questionId={moduleData.id}
        fluxStore={fluxStore}
      />
      <QuestionComponent conditionalAlerts={conditionalAlerts} error={error} />
      {currentQuestion?.writeback?.enabled && (
        <div className={moduleCss.multipleChoiceModule_writebackHint}>
          <WarningIcon className={moduleCss.multipleChoiceModule_hintIcon} />
          Responses will overwrite ATS values
        </div>
      )}
    </div>
  );
};

export default FieldWritebackModule;
